import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import moment from 'moment-timezone';

import { useTeamMemberDetailsForSelect } from '@hooks/useTeamMemberDetailsForSelect';
import {
  useUrlDateRangeSync,
  useUrlFilterWrite,
} from '@hooks/useUrlFilterSync';
import { getInitialReportDateRangeFromSearch } from '@utils/reportFiltersUrl';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import { useFocusVsDistraction } from '@hooks/useFocusVsDistraction';
import { queryKeys } from '@config/queryKeys';
import alertsService from '@services/alerts';
import {
  fetchConsolidatedDay,
  fetchConsolidatedDays,
} from '@services/teamConsolidated';

import {
  buildOverallProductivity,
  buildKeyHighlights,
  buildProductivityOverTime,
  buildChartTicks,
  buildActivityBreakdown,
  buildTopApplications,
  buildAttendanceSummary,
  buildTeamPerformanceRows,
  buildActivityHeatmap,
} from '../TeamDashboard.helpers';
import { buildTeamInsights } from '../TeamDashboard.insights';

export function useTeamDashboardData() {
  const team = useSelector((s) => s.team);
  // `activeTeam` is populated on every mount by NavItem's GET_USER_ACTIVE_TEAM;
  // `active` only gets set by an explicit team-switcher action, so it can stay
  // empty for a whole session (e.g. a Client, who has no team-switcher UI).
  const teamId = team?.activeTeam?.activeTeamid || team?.active?.activeTeamid;
  const companyId = useSelector(
    (s) => s.auth.profile?.activeCompany?.companyId
  );
  // URL-synced (no member/team selector on this page — team is ambient via
  // Redux — so only the date range needs to persist across refresh/nav).
  const location = useLocation();
  const history = useHistory();
  const [dateRange, setDateRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );

  useUrlDateRangeSync({ location, setDateRange });
  useUrlFilterWrite({
    history,
    location,
    params: { start: dateRange.start, end: dateRange.end },
    enabled: Boolean(teamId),
  });

  // Cached member roster (React Query, 2-min stale) — no extra Firestore cost,
  // shared with other views that already loaded it.
  const { memberDetails } = useTeamMemberDetailsForSelect(teamId);

  const [rawToday, setRawToday] = useState(null);
  const [rawYesterday, setRawYesterday] = useState(null);
  const [rawLast7, setRawLast7] = useState([]);
  const [rawAlerts, setRawAlerts] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const { data: appNameMapping = {} } = useQuery({
    queryKey: queryKeys.applicationInfo.mapping,
    queryFn: fetchApplicationInfoMapping,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // ── 3 Firestore reads, all in parallel ───────────────────────────────────
  // today + yesterday + last-7 = 1 round-trip latency, not 3 sequential.
  const fetchAll = useCallback(async () => {
    if (!teamId) return;
    setRawToday(null);
    setRawYesterday(null);
    setRawLast7([]);
    setIsFetching(true);
    try {
      const selectedDate = dateRange.start;
      const prevDate = moment(selectedDate)
        .subtract(1, 'day')
        .format('YYYY-MM-DD');

      const [todayDoc, yesterdayDoc, last7Docs] = await Promise.all([
        fetchConsolidatedDay(teamId, selectedDate),
        fetchConsolidatedDay(teamId, prevDate),
        fetchConsolidatedDays(teamId, 7),
      ]);
      setRawToday(todayDoc);
      setRawYesterday(yesterdayDoc);
      setRawLast7(last7Docs);
    } catch (_) {
      // Widgets fall back to empty-state on any error
    } finally {
      setIsFetching(false);
    }
  }, [teamId, dateRange.start]);

  const fetchAlerts = useCallback(async () => {
    if (!teamId) return;
    try {
      const { success, data } = await alertsService.getTeamRecentAlerts(
        teamId,
        { limit: 5 }
      );
      if (success) setRawAlerts(data);
    } catch (_) {}
  }, [teamId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // ── Derived display data ──────────────────────────────────────────────────
  // Each memo re-runs only when its specific inputs change — no unnecessary
  // downstream re-renders when an unrelated piece of state updates.

  const timezone = useMemo(() => moment.tz.guess(), []);

  const overallProductivityData = useMemo(
    () => buildOverallProductivity(rawToday, rawYesterday, rawLast7),
    [rawToday, rawYesterday, rawLast7]
  );

  const { data: focusVsDistractionData } = useFocusVsDistraction(
    rawToday?.appUsage,
    rawToday?.totalSec,
    rawToday?.productiveSec,
    { companyId, teamId }
  );

  // Yesterday's focus % isn't shown anywhere on its own — it only exists to
  // give the "Focus Time Trend" insight something to compare today against.
  const { data: focusVsDistractionYesterdayData } = useFocusVsDistraction(
    rawYesterday?.appUsage,
    rawYesterday?.totalSec,
    rawYesterday?.productiveSec,
    { companyId, teamId }
  );

  const keyHighlightsData = useMemo(
    () => buildKeyHighlights(rawToday, rawYesterday, focusVsDistractionData),
    [rawToday, rawYesterday, focusVsDistractionData]
  );

  const productivityOverTimeData = useMemo(
    () => buildProductivityOverTime(rawToday, rawLast7, timezone),
    [rawToday, rawLast7, timezone]
  );

  const chartTicks = useMemo(
    () => buildChartTicks(rawToday, timezone),
    [rawToday, timezone]
  );

  const activityBreakdownData = useMemo(
    () => buildActivityBreakdown(rawToday),
    [rawToday]
  );

  const topApplicationsData = useMemo(
    () => buildTopApplications(rawToday?.appUsage, appNameMapping),
    [rawToday, appNameMapping]
  );

  const attendanceSummaryData = useMemo(
    () => buildAttendanceSummary(rawToday),
    [rawToday]
  );

  const teamPerformanceData = useMemo(
    () =>
      buildTeamPerformanceRows(
        rawToday?.top5Performers,
        memberDetails,
        rawYesterday,
        rawLast7
      ),
    [rawToday, memberDetails, rawYesterday, rawLast7]
  );

  const heatmapData = useMemo(
    () => buildActivityHeatmap(rawLast7, dateRange.start, timezone),
    [rawLast7, dateRange.start, timezone]
  );

  const insightsData = useMemo(
    () =>
      buildTeamInsights({
        today: rawToday,
        yesterday: rawYesterday,
        last7: rawLast7,
        top5Performers: rawToday?.top5Performers,
        memberDetails,
        timezone,
        todayFocusPct: focusVsDistractionData?.focusPct,
        yesterdayFocusPct: focusVsDistractionYesterdayData?.focusPct,
        topApplications: topApplicationsData,
      }),
    [
      rawToday,
      topApplicationsData,
      rawYesterday,
      rawLast7,
      memberDetails,
      timezone,
      focusVsDistractionData,
      focusVsDistractionYesterdayData,
    ]
  );

  const recentAlertsData = useMemo(() => {
    if (!rawAlerts) return null;
    return rawAlerts.map((alert) => ({
      id: alert.id,
      iconType: alert.severity === 'low' ? 'bar-chart' : 'triangle',
      iconBg: alert.severity === 'high' ? '#FFF1F3' : '#FFF6ED',
      iconColor: alert.severity === 'high' ? '#ef4444' : '#f59e0b',
      title: alert.title || 'Alert',
      subtitle: alert.metadata?.userEmailId || '',
      time: alertsService.formatDate(alert.createdAt),
    }));
  }, [rawAlerts]);

  return {
    teamId,
    dateRange,
    setDateRange,
    // Mapped to both flags so the component interface stays unchanged
    isFetchingStats: isFetching,
    isFetchingAttendance: isFetching,
    overallProductivityData,
    keyHighlightsData,
    productivityOverTimeData,
    chartTicks,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    attendanceSummaryData,
    teamPerformanceData,
    heatmapData,
    recentAlertsData,
    insightsData,
  };
}
